import React, { useState } from 'react';
import { Badge, Button, Divider, Empty, List, Popover, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { brandSemantic } from '../config/brand';
import { formatDateTime } from '../utils/formatters';

const { Text } = Typography;

const toDate = (timestamp) => new Date(String(timestamp).replace(' ', 'T'));

export const formatNotificationTimestamp = (timestamp) => {
  const date = toDate(timestamp);
  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMinutes >= 0 && diffMinutes < 24 * 60) {
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  return formatDateTime(timestamp);
};

export default function NotificationCentre({
  notifications = [],
  currentUser,
  onMarkRead,
  onMarkAllRead,
  onNavigate,
  t = (key) => key,
}) {
  const [panelOpen, setPanelOpen] = useState(false);

  const userNotifications = notifications.filter((n) => n.targetUserId === currentUser?.id);
  const visibleNotifications = [...userNotifications]
    .sort((a, b) => toDate(b.timestamp) - toDate(a.timestamp))
    .slice(0, 50);
  const unreadCount = userNotifications.filter((n) => !n.read).length;

  const handleEntryClick = (notification) => {
    setPanelOpen(false);
    onNavigate?.(notification);
  };

  const content = (
    <div style={{ width: 360 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 4px 8px' }}>
        <Text strong>{t('Notifications')}</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={onMarkAllRead}>
            {t('Mark all as read')}
          </Button>
        )}
      </div>
      <Divider style={{ margin: '0 0 8px' }} />
      {visibleNotifications.length === 0 ? (
        <Empty description={t('No notifications')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '16px 0' }} />
      ) : (
        <List
          dataSource={visibleNotifications}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          renderItem={(notification) => (
            <List.Item
              key={notification.id}
              onClick={() => handleEntryClick(notification)}
              style={{
                cursor: 'pointer',
                padding: '10px 8px',
                background: notification.read ? 'transparent' : brandSemantic.infoSoft,
                borderRadius: 8,
              }}
            >
              <div style={{ width: '100%' }}>
                <Text style={{ fontSize: 13 }}>{notification.message}</Text>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {formatNotificationTimestamp(notification.timestamp)}
                  </Text>
                  {!notification.read && (
                    <span
                      aria-hidden="true"
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: brandSemantic.info,
                        display: 'inline-block',
                      }}
                    />
                  )}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      open={panelOpen}
      onOpenChange={setPanelOpen}
      trigger="click"
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" offset={[-2, 4]}>
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          aria-label="Notifications"
        />
      </Badge>
    </Popover>
  );
}
